import React, { useRef, useState, useCallback, useEffect } from "react";

/**
 * BeforeAfterSlider — clean, dependency-free drag slider.
 * Both images must have the same aspect ratio for best results.
 */
export const BeforeAfterSlider = ({
  beforeSrc,
  afterSrc,
  beforeLabel = "Before",
  afterLabel = "After",
  aspectClass = "aspect-[16/10]",
  ...rest
}) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const onMove = useCallback(
    (clientX) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      setPos((x / rect.width) * 100);
    },
    []
  );

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      onMove(cx);
    };
    const stop = () => setDragging(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", handleMove, { passive: true });
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", stop);
    };
  }, [dragging, onMove]);

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className={`relative ${aspectClass} w-full overflow-hidden rounded-2xl bg-slate-100 select-none cursor-ew-resize`}
      onMouseDown={(e) => {
        setDragging(true);
        onMove(e.clientX);
      }}
      onTouchStart={(e) => {
        setDragging(true);
        onMove(e.touches[0].clientX);
      }}
      {...rest}
    >
      <img src={afterSrc} alt={afterLabel} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img src={beforeSrc} alt={beforeLabel} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      </div>
      <span className="absolute top-4 left-4 text-xs uppercase tracking-[0.18em] font-semibold text-white bg-slate-900/70 backdrop-blur px-3 py-1.5 rounded-full">
        {beforeLabel}
      </span>
      <span className="absolute top-4 right-4 text-xs uppercase tracking-[0.18em] font-semibold text-white bg-blue-600/90 backdrop-blur px-3 py-1.5 rounded-full">
        {afterLabel}
      </span>
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.08)] pointer-events-none"
        style={{ left: `${pos}%` }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-white shadow-xl flex items-center justify-center pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" className="text-slate-900">
          <path fill="currentColor" d="M8 7l-5 5 5 5V7zm8 0v10l5-5-5-5z" />
        </svg>
      </div>
    </div>
  );
};
